<template>
  <div class="my-6">
    <h3 class="font-bold">Sistema de Suporte</h3>
    <span class="text-sm text-gray-500">Aqui você pode observar todos os chamados de suporte dos usuários como suas respectivas ações e status.</span>
  </div>
  <hr class="my-9" />
  <div class="card shadow-lg border rounded-lg p-6 bg-white mt-10">
    <div class="w-full">
      <div class="flex flex-col lg:flex-row items-end">
        <div class="w-full p-2">
          <h3 class="font-bold">Gerenciar Chamados de Suporte</h3>
          <span class="text-sm text-gray-500">
            Aqui você pode visualizar, filtrar e gerenciar todos os chamados de suporte abertos pelos usuários da plataforma.
          </span>
        </div>
      </div>

      <!-- Filtros -->
      <div class="flex lg:flex-row flex-col items-center mt-3 py-3">
        <div class="w-full p-2">
          <Input type="datalist" id="evento" v-model="eventoId" label="Evento" placeholder="Selecione um evento" name="evento"
            :data="eventos" @keyupSearch="getEvent" />
        </div>
        <div class="w-full p-2">
          <Input type="select" id="status" v-model="statusSelect" label="Status" placeholder="Selecione um Status"
            :data="statusOptions" @keyupSearch="getStatus" />
        </div>
        <div class="w-full p-2">
          <Input type="select" id="tipo" v-model="tipoSelect" label="Tipo"
            placeholder="Selecione o Tipo" :data="tipoOptions" @keyupSearch="getTipo" />
        </div>
      </div>

      <div class="flex flex-col items-end">
        <div class="justify-center">
          <Button color="primary" :text="`Filtrar`" @click="filtrar()" id="filtrar"></Button>
        </div>
      </div>
      <div class="card w-full p-2" v-if="data">
        <DataTable :headers="headers" :data="data" :options="options" color="primary" />
      </div>
    </div>
  </div>
</template>

<script>
import DataTable from "../../components/Elements/Datatable.vue";
import Button from "../../components/Elements/Button.vue";
import { GET, PUT } from "../../services/api";
import { inject, onMounted, ref, computed } from "vue";
import { formatarDataSomente } from "../../services/timezone";
import { useRouter } from "vue-router";
import { toDate } from "../../services/formater";
import Input from '@/components/Elements/Input.vue';

export default {
  name: 'SuporteIndex',
  components: {
    DataTable,
    Button,
    Input,
  },

  setup() {
    const router = useRouter();
    const data = ref([]);
    const alert = inject("alert");
    const loader = inject("loading");
    const modal = inject("modal");

    // Estados dos filtros
    const eventoId = ref('');
    const eventos = ref([]);
    const eventosOriginal = ref([]);
    
    const statusSelect = ref('aberto');
    const statusOptions = ref([
      { label: 'Todos', value: '' },
      { label: 'Aberto', value: 'aberto' },
      { label: 'Encerrado', value: 'encerrado' }
    ]);

    const tipoSelect = ref('');
    const tipoOptions = ref([
      { label: 'Todos', value: '' },
      { label: 'Criar Ingresso', value: 'criar_ingresso' },
      { label: 'Alterar Lote', value: 'alterar_lote' },
      { label: 'Outros', value: 'outros' }
    ]);

    const filtro = ref({
      eventoId: null,
      status: 'aberto',
      tipo: null
    });

    // Funções utilitárias
    const getStatusBadge = (status) => {
      const statusConfig = {
        'aberto': { label: 'Aberto', class: 'bg-blue-100 text-blue-800' },
        'encerrado': { label: 'Encerrado', class: 'bg-gray-100 text-gray-800' }
      };
      return statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    };

    const getTipoBadge = (tipo) => {
      const tipoConfig = {
        'criar_ingresso': { label: 'Criar Ingresso', class: 'bg-green-100 text-green-800' },
        'alterar_lote': { label: 'Alterar Lote', class: 'bg-orange-100 text-orange-800' },
        'outros': { label: 'Outros', class: 'bg-blue-100 text-blue-800' }
      };
      return tipoConfig[tipo] || { label: tipo, class: 'bg-gray-100 text-gray-800' };
    };

    const formatarData = (dataString) => {
      if (!dataString) return '-';
      return formatarDataSomente(dataString);
    };

    // Funções de API
    const getEventos = async () => {
      try {
        const response = await GET("event");
        eventosOriginal.value = response;
        eventos.value = [
          { label: 'Todos os eventos', value: null },
          ...response.map(evento => ({ 
            label: evento.nome + " - " + toDate(evento.data), 
            value: evento.id 
          }))
        ];
      } catch (error) {
        console.error('Erro ao buscar eventos:', error);
        alert.open("Erro", "Não foi possível carregar os eventos", "danger");
      }
    };

    const filtrar = async () => {
      try {
        loader.open();
        
        // Preparar parâmetros de consulta baseados na API real
        const queryParams = new URLSearchParams();
        
        // Evento selecionado
        const eventoSelecionado = eventos.value.find(e => e.label === eventoId.value);
        if (eventoSelecionado && eventoSelecionado.value) {
          queryParams.append('evento_id', eventoSelecionado.value.toString());
        }
        
        // Status - só adiciona se não for vazio (todos)
        if (statusSelect.value && statusSelect.value !== '') {
          queryParams.append('status', statusSelect.value);
        }
        
        // Tipo - só adiciona se não for vazio (todos) 
        if (tipoSelect.value && tipoSelect.value !== '') {
          queryParams.append('tipo', tipoSelect.value);
        }
        
        // Fazer requisição com query parameters
        const url = `/admin/chamados${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        const response = await GET(url);
        
        data.value = response.data.map(chamado => {
          const evento = eventosOriginal.value.find(e => e.id === chamado.eventoId);
          return {
            id: chamado.id,
            status: getStatusBadge(chamado.status).label,
            tipo: getTipoBadge(chamado.tipo).label,
            evento: evento?.nome || '-',
            usuario: chamado.usuario?.nome || '-',
            titulo: chamado.assunto, // API usa 'assunto' ao invés de 'titulo'
            descricao: chamado.descricao?.substring(0, 50) + '...' || '-',
            createdAt: formatarData(chamado.createdAt),
            updatedAt: formatarData(chamado.updatedAt),
            _original: chamado
          };
        });

        loader.close();
      } catch (error) {
        loader.close();
        console.error('Erro ao filtrar chamados:', error);
        alert.open("Erro", "Não foi possível filtrar os chamados", "danger");
      }
    };

    // Ações da tabela
    const visualizarChamado = (param) => {
      router.push(`/suporte/detalhes/${param.id}`);
    };

    const alterarStatus = async (param, novoStatus) => {
      loader.open();
      
      const alterarStatusFunction = async function () {
        try {
          loader.open();
          await PUT(`admin/chamados/${param.id}/status`, { status: novoStatus });
          
          // Atualizar localmente
          const index = data.value.findIndex(item => item.id === param.id);
          if (index !== -1) {
            data.value[index].status = getStatusBadge(novoStatus).label;
            data.value[index].updatedAt = formatarData(new Date().toISOString());
          }
          
          loader.close();
          alert.open("Sucesso!", `Status alterado para ${getStatusBadge(novoStatus).label}`, "success");
        } catch (error) {
          loader.close();
          console.error('Erro ao alterar status:', error);
          alert.open("Erro", "Não foi possível alterar o status", "danger");
        }
      };

      modal.open(
        "Confirmar Alteração",
        `Deseja alterar o status para "${getStatusBadge(novoStatus).label}"?`,
        "warning",
        "Confirmar",
        () => alterarStatusFunction(),
        modal.close()
      );
      
      loader.close();
    };

    const exportarRelatorio = async () => {
      try {
        loader.open();
        // TODO: Implementar exportação de relatório
        alert.open("Info", "Funcionalidade de exportação será implementada em breve", "info");
        loader.close();
      } catch (error) {
        loader.close();
        alert.open("Erro", "Erro ao exportar relatório", "danger");
      }
    };

    // Inicialização
    onMounted(async () => {
      await getEventos();
      await filtrar(); // Carregar chamados em aberto por padrão
    });

    // Definições da tabela
    const headers = [
      { key: "ID", value: "id" },
      { key: "Status", value: "status" },
      { key: "Tipo", value: "tipo" },
      { key: "Evento", value: "evento" },
      { key: "Usuário", value: "usuario" },
      { key: "Título", value: "titulo" },
      { key: "Criado em", value: "createdAt" },
      { key: "Atualizado em", value: "updatedAt" },
      { key: "Ações", value: "" },
    ];

    const options = [
      { 
        icon: "eye", 
        title: "Visualizar chamado", 
        action: (param) => visualizarChamado(param) 
      },
      { 
        icon: "times", 
        title: "Encerrar chamado", 
        action: (param) => alterarStatus(param, 'encerrado') 
      },
    ];

    return {
      data,
      headers,
      options,
      eventos,
      eventoId,
      statusSelect,
      statusOptions,
      tipoSelect,
      tipoOptions,
      filtrar,
      exportarRelatorio,
      filtro,
    };
  }
}
</script>

<style scoped>
.badge {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}
</style>